<script setup lang="ts">
import type { UIIconProps } from '@core/types'
import { computed, useAttrs, parseUnit, ICON_COMPONENTS, useTheme, useDefaults } from '#imports'
import type { ColorBaseToken, RGBColor } from '#theme'

const attrs = useAttrs()
const _props = defineProps<UIIconProps>()
const props = useDefaults({
    props: _props,
    defaults: {
        size: '18px',
        rotated: false
    }
})

const { theme, themeClass } = useTheme(() => ({
    color: props.color
}))

const style = computed(() => {
    const styles: Partial<{
        color: string
        width: string
    }> = {
        width: props.size ? `${parseUnit(props.size, 'px')}px` : undefined
    }

    if (!isColorClass.value) {
        styles['color'] = props.color as RGBColor
    }
    else {
        styles['color'] = 'var(--color)'
    }

    return styles
})

const SvgIcon = computed(() => {
    if (props.name.startsWith('$')) {
        const name = theme.value.icons?.[props.name as keyof typeof theme.value.icons]

        if (!name) {
            throw new Error(`Icon alias ${name} is not defined.`)
        }

        return ICON_COMPONENTS[name as keyof typeof ICON_COMPONENTS]
    }

    return ICON_COMPONENTS[props.name as keyof typeof ICON_COMPONENTS]
})

const isColorClass = computed(() => {
    if (!props.color) return false

    return !!theme.value.colors?.[props.color as ColorBaseToken]
})
</script>

<template>
    <span
        :class="{
            [`icon icon--${props.name}`]: true,
            'icon--clickable': !!attrs.onClick,
            'icon--rotated': props.rotated,
            ...themeClass
        }"
        :style="style"
    >
        <component :is="SvgIcon" />
    </span>
</template>

<style scoped lang="scss">
.icon {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    color: inherit;
    transition: transform 0.25s ease-in-out;

    :deep(svg) {
        height: auto;
        width: auto;
        max-width: 100%;
        max-height: 100%;
        vertical-align: middle;
        width: auto;

        path,
        rect,
        circle {
            fill: currentColor;
        }
    }

    &--clickable {
        cursor: pointer;
        color: var(--color, $brand-primary);
        transition: color 0.25s ease-in-out;
    }

    &--rotated {
        transform: rotate(-180deg);
    }

    &::before,
    &::after {
        display: none;
    }

    &--big {
        font-size: 120% !important;
        line-height: 120%;
    }

    &--medium {
        font-size: 80% !important;
    }

    &--small {
        font-size: 70% !important;
    }

    &--silhouette {
        opacity: 1 !important;
    }
}
</style>
